import apiInstance from "api/axios";
import { Requests } from "config/api";
import { CarMakeType, CarType } from "types/loadedData";

export type ExternalImportProvider = "Mekina" | "Gari" | "Facebook";

export type ExternalImportPreview = {
  source: ExternalImportProvider;
  sourceId: string;
  sourceUrl: string;
  sourcePublishedAt?: string;
  sourceCategory: string;
  classificationSource: string;
  title: string;
  makeName: string;
  model: string;
  year?: number;
  price?: number;
  fuelType: string;
  transmissionType: string;
  bodyType: string;
  color: string;
  location: string;
  mileage?: number;
  usageType: "new" | "used";
  listingType: "Sale" | "Rent";
  description: string;
  images: string[];
  sellerName: string;
  sellerPhone: string;
  sellerType: string;
  matchedMake?: CarMakeType | null;
  duplicateCar?: Pick<CarType, "_id" | "tag" | "model" | "year"> | null;
};

export type MekinaPublishPayload = {
  sourceUrl: string;
  make: string;
  model: string;
  year: string | number;
  price: string | number;
  fuelType: string;
  transmissionType: string;
  color: string;
  location: string;
  mileage?: string | number;
  usageType: "new" | "used";
  listingType: "Sale" | "Rent";
  description: string;
  images: string[];
  rightsConfirmed: boolean;
};

export type GariPublishPayload = Omit<MekinaPublishPayload, "images"> & {
  jobId: string;
  images: string[];
  sellerName?: string;
  sellerPhone?: string;
};

export type GariImportJobStatus = "Pending" | "Processing" | "Ready" | "Failed";

export type GariImportJob = {
  _id: string;
  sourceUrl: string;
  sourceId: string;
  status: GariImportJobStatus;
  listing?: ExternalImportPreview | null;
  error?: string;
  attempts?: number;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
};

export type ExternalImportSource = {
  provider: ExternalImportProvider;
  externalId?: string;
  url: string;
  publishedAt?: string;
  importedAt?: string;
  sellerName?: string;
  sellerPhone?: string;
};

export const previewMekinaImport = async (
  sourceUrl: string
): Promise<ExternalImportPreview> => {
  const response = await apiInstance.post(Requests.post.previewMekinaImport, {
    sourceUrl,
  });
  return response.data?.listing;
};

export const previewGariImport = async (
  sourceUrl: string
): Promise<GariImportJob> => {
  const response = await apiInstance.post(Requests.post.previewGariImport, {
    sourceUrl,
  });
  return response.data?.job;
};

export const previewFacebookImport = async (
  sourceUrl: string,
  caption: string
): Promise<ExternalImportPreview> => {
  const response = await apiInstance.post(Requests.post.previewFacebookImport, { sourceUrl, caption });
  return response.data?.listing;
};

export const publishFacebookImport = async (
  payload: Omit<MekinaPublishPayload, "images"> & { sellerName: string; sellerPhone: string; jobId?: string; selectedImages?: string[] },
  photos: File[]
): Promise<CarType> => {
  const data = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined) data.append(key, Array.isArray(value) ? JSON.stringify(value) : String(value));
  });
  photos.forEach((photo) => data.append("images", photo));
  const response = await apiInstance.post(Requests.post.publishFacebookImport, data);
  return response.data?.car;
};

export const createFacebookImportJob = async (sourceUrl: string): Promise<GariImportJob> => {
  const response = await apiInstance.post(Requests.post.createFacebookImportJob, { sourceUrl });
  return response.data?.job;
};

export const getFacebookImportJob = async (id: string): Promise<GariImportJob> => {
  const response = await apiInstance.get(Requests.get.getFacebookImportJob.replace(":id", id));
  return response.data?.job;
};

export const getGariImportJob = async (id: string): Promise<GariImportJob> => {
  const route = Requests.get.getGariImportJob.replace(":id", id);
  const response = await apiInstance.get(route);
  return response.data?.job;
};

export const publishMekinaImport = async (
  payload: MekinaPublishPayload
): Promise<CarType> => {
  const response = await apiInstance.post(
    Requests.post.publishMekinaImport,
    payload
  );
  return response.data?.car;
};

export const publishGariImport = async (
  payload: GariPublishPayload
): Promise<CarType> => {
  const response = await apiInstance.post(
    Requests.post.publishGariImport,
    payload
  );
  return response.data?.car;
};

export const getExternalImportSource = async (
  id: string
): Promise<ExternalImportSource> => {
  const route = Requests.get.getExternalCarSource.replace(":id", id);
  const response = await apiInstance.get(route);
  return response.data?.source;
};
